// src/components/ListingDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getListingDetail, refreshImageUrls } from '../services/api';

import Footer from './ContactFooter';

function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // 1) State for the main listing and loading state
  const [listing, setListing] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 2) Lightbox / photo state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [allPhotos, setAllPhotos] = useState([]);

  // 3) Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [gdpr, setGdpr] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [listingName, setListingName] = useState('');

  // Get device width for responsive styles
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // ---------------------------------
  // Hook #1: Fetch listing data by ID
  // ---------------------------------
  useEffect(() => {
    const loadListing = async () => {
      try {
        setIsLoading(true);
        console.log(`Fetching listing data for ID: ${id}`);
        
        // Direct fetch without URL refresh - we use public URLs now!
        const data = await getListingDetail(id);
        setListing(data);
        
        // Set initial contact form data
        setName('');
        setEmail('');
        setMessage('');
        setListingName(data?.name || '');
      } catch (error) {
        setError('Failed to load listing');
        console.error('Error loading listing:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadListing();
    }
  }, [id]);

  // Function to get image URL from a listing image object - simplified for triple dash consistency
  const getImageUrl = (imageObj) => {
    if (!imageObj) {
      console.log('No image object provided');
      return 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22100%22%2F%3E%3Ccircle%20fill%3D%22%23aaa%22%20cx%3D%22100%22%20cy%3D%22100%22%20r%3D%2250%22%2F%3E%3Ccircle%20fill%3D%22%23888%22%20cx%3D%22500%22%20cy%3D%22300%22%20r%3D%2270%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';
    }

    console.log('Getting image URL for:', imageObj);

    // Priority 1: fileUrl (direct Google Cloud Storage URL with triple dashes)
    if (imageObj.fileUrl && imageObj.fileUrl.includes('storage.googleapis.com')) {
      console.log('Using direct GCS fileUrl:', imageObj.fileUrl);
      return imageObj.fileUrl;
    }

    // Priority 2: gcsBlobName
    if (imageObj.gcsBlobName) {
      const gcsUrl = `https://storage.googleapis.com/eurocentrumpd-bucket/${imageObj.gcsBlobName}`;
      console.log('Constructed GCS URL from gcsBlobName:', gcsUrl);
      return gcsUrl;
    }

    // Priority 3: filename
    if (imageObj.filename) {
      const gcsUrl = `https://storage.googleapis.com/eurocentrumpd-bucket/${imageObj.filename}`;
      console.log('Constructed GCS URL from filename:', gcsUrl);
      return gcsUrl;
    }

    console.log('No valid image URL found, using placeholder');
    return 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22100%22%2F%3E%3Ccircle%20fill%3D%22%23aaa%22%20cx%3D%22100%22%20cy%3D%22100%22%20r%3D%2250%22%2F%3E%3Ccircle%20fill%3D%22%23888%22%20cx%3D%22500%22%20cy%3D%22300%22%20r%3D%2270%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';
    }
  
  // Get URLs for all images
  useEffect(() => {
    if (listing && listing.images && listing.images.length > 0) {
      console.log('Processing listing images to get URLs');
      
      // Map all images to their URLs
      const imageUrls = listing.images.map((img, index) => {
        const url = getImageUrl(img);
        console.log(`Image ${index+1} URL:`, url);
        return {
          original: url,
          thumbnail: url
        };
      });
      
      setAllPhotos(imageUrls);
      console.log('Set allPhotos with processed URLs:', imageUrls);
    } else {
      // Initialize with empty array if no images
      console.log('No images found in listing, setting allPhotos to empty array');
      setAllPhotos([]);
    }
  }, [listing]);

  // ---------------------------------
  // Hook #3: Handle keyboard navigation for lightbox
  // ---------------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen || !allPhotos.length) return;
      
      if (e.key === 'ArrowLeft') {
        setCurrentIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);
      } else if (e.key === 'ArrowRight') {
        setCurrentIndex((prev) => (prev + 1) % allPhotos.length);
      } else if (e.key === 'Escape') {
        setLightboxOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up event listener on unmount or when lightbox closes
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxOpen, allPhotos.length]);

  // Effect for textarea auto-resize
  useEffect(() => {
    const textareas = document.querySelectorAll('.autoExpand');
    
    const adjustHeight = (element) => {
      element.style.height = 'auto';
      element.style.height = (element.scrollHeight) + 'px';
    };
    
    textareas.forEach(textarea => {
      adjustHeight(textarea);
      textarea.addEventListener('input', function() {
        adjustHeight(this);
      });
    });
    
    return () => {
      textareas.forEach(textarea => {
        textarea.removeEventListener('input', function() {
          adjustHeight(this);
        });
      });
    };
  }, []);

  // Get device width for responsive styles
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Is mobile view
  const isMobile = windowWidth <= 768;

  // If listing not loaded, show loading
  if (!listing) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}></div>
        <p>Načítavam inzerát...</p>
      </div>
    );
  }

  // LIGHTBOX controls
  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);
  const goPrev = (e) => {
    e.stopPropagation();
    if (!allPhotos.length) return;
    setCurrentIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);
  };
  const goNext = (e) => {
    e.stopPropagation();
    if (!allPhotos.length) return;
    setCurrentIndex((prev) => (prev + 1) % allPhotos.length);
  };

  // Get the tags from the listing
  const tagsList = listing.tags || [];

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'name') setName(value);
    else if (name === 'email') setEmail(value);
    else if (name === 'message') setMessage(value);
    else if (name === 'gdpr') setGdpr(checked);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset submit message
    setSubmitMessage({ type: '', text: '' });
    
    // Validate form
    if (!name || !email || !message) {
      setSubmitMessage({
        type: 'error',
        text: 'Prosím vyplňte všetky povinné polia.'
      });
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubmitMessage({
        type: 'error',
        text: 'Prosím zadajte platný email.'
      });
      return;
    }
    
    // Check GDPR compliance
    if (!gdpr) {
      setSubmitMessage({
        type: 'error',
        text: 'Prosím potvrďte súhlas so spracovaním osobných údajov.'
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Display message while submitting
      setSubmitMessage({
        type: 'info',
        text: 'Odosielanie správy...'
      });
      
      // Prepare listing info
      const listingInfo = listing ? 
        `${listing.name} ${listing.location ? `(${listing.location})` : ''} ${listing.price ? `- ${listing.price}` : ''}` : 
        'Nehnuteľnosť';
        
      console.log('Submitting form with listing info:', listingInfo);
      
      // Try new simplified endpoint first
      try {
        const response = await fetch('https://eurocentrumv1.onrender.com/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: 'eurocentrum.reality@gmail.com', // Updated recipient email
            subject: `Dopyt: ${listing.name}`,
            text: `Meno: ${name}\nEmail: ${email}\n\nNehnuteľnosť: ${listingInfo}\n\nSpráva:\n${message}`,
            html: `
              <h2>Nový dopyt na nehnuteľnosť</h2>
              <p><strong>Meno:</strong> ${name}</p>
              <p><strong>Email:</strong> ${email}</p>
              <p><strong>Nehnuteľnosť:</strong> ${listingInfo}</p>
              <p><strong>Správa:</strong></p>
              <p>${message.replace(/\n/g, '<br>')}</p>
            `
          }),
        });
        
        console.log('Response status:', response.status);
        const result = await response.json();
        console.log('Response data:', result);
        
        if (response.ok) {
          // Success
          setSubmitMessage({
            type: 'success',
            text: 'Správa bola úspešne odoslaná! Ďakujeme za Váš záujem.'
          });
          // Reset form
          setName('');
          setEmail('');
          setMessage('');
          setGdpr(false);
          return;
        }
      } catch (directError) {
        console.error('Error with direct endpoint, falling back to original:', directError);
      }
      
      // Fallback to original endpoint if direct one fails
      // Prepare form data
      const formData = {
        name,
        email,
        subject: `Dopyt: ${listing.name}`,
        message,
        gdpr,
        listingInfo
      };
      
      console.log('Falling back to original endpoint with data:', formData);
      
      // Send the email
      const response = await fetch('https://eurocentrumv1.onrender.com/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      console.log('Original endpoint response status:', response.status);
      const result = await response.json();
      console.log('Original endpoint response data:', result);
      
      if (response.ok) {
        // Success
        setSubmitMessage({
          type: 'success',
          text: 'Správa bola úspešne odoslaná! Ďakujeme za Váš záujem.'
        });
        // Reset form
        setName('');
        setEmail('');
        setMessage('');
        setGdpr(false);
      } else {
        // Error from server
        setSubmitMessage({
          type: 'error',
          text: result.message || 'Nastala chyba pri odosielaní správy.'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage({
        type: 'error',
        text: 'Nastala chyba pri odosielaní správy. Skúste to znova neskôr.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to go back to the previous page
  const handleGoBack = (e) => {
    e.preventDefault();
    // Use navigate(-1) which causes the browser to go back
    // The ScrollRestorationProvider will handle restoring the scroll position
    navigate(-1);
  };

  return (
    <>
      <div style={{
        ...styles.container,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {/* Back Button */}
        <div style={styles.backButtonContainer}>
          <button 
            onClick={handleGoBack}
            style={styles.backButton}
          >
            ← Späť na zoznam
          </button>
        </div>

        {/* Breadcrumb */}
        <div style={styles.breadcrumb}>
          <a href="/" style={styles.breadcrumbLink}>Domov</a>
          <span style={styles.breadcrumbSeparator}>/</span>
          <span>{listing.name || 'Listing Name'}</span>
        </div>

        <div style={styles.mainContent}>
          {/* Left Side - Images and Details */}
          <div style={styles.leftSide}>
            {/* Title */}
            <h1 style={styles.mainHeading}>{listing.name || 'Listing Name'}</h1>

            {/* Tags Section - same logic as in ListingCard */}
            {tagsList.length > 0 && (
              <div style={styles.tagContainer}>
                {tagsList.sort((a, b) => {
                  const aLower = a.toLowerCase();
                  const bLower = b.toLowerCase();
                  
                  // Define priority groups
                  const isAAktualne = aLower === 'aktuálne' || aLower === 'aktualne';
                  const isBAktualne = bLower === 'aktuálne' || bLower === 'aktualne';
                  
                  // Sale type tags
                  const isASaleType = ['predaj', 'prenájom', 'prenajom', 'predané', 'prenajaté'].includes(aLower);
                  const isBSaleType = ['predaj', 'prenájom', 'prenajom', 'predané', 'prenajaté'].includes(bLower);
                  
                  // First priority: aktualne
                  if (isAAktualne && !isBAktualne) return -1;
                  if (!isAAktualne && isBAktualne) return 1;
                  
                  // Second priority: sale type tags
                  if (isASaleType && !isBSaleType) return -1;
                  if (!isASaleType && isBSaleType) return 1;
                  
                  // If both are sale types, maintain their relative order
                  if (isASaleType && isBSaleType) return 0;
                  
                  // For all other tags, use alphabetical order
                  return aLower.localeCompare(bLower);
                }).map(tag => (
                  <div
                    key={tag}
                    style={
                      (tag === 'aktuálne' || tag === 'aktualne') ? styles.tagAktualne :
                      tag === 'predané' ? styles.tagPredane :
                      tag === 'prenajaté' ? styles.tagPrenajate :
                      tag === 'neplatiči' ? styles.tagNeplatici :
                      styles.tag
                    }
                  >
                    {tag === 'prenajom' || tag === 'prenájom' ? 'na nájom' : 
                     tag === 'predaj' ? 'na predaj' :
                     tag === 'prenajaté' ? 'prenajaté' :
                     (tag === 'aktuálne' || tag === 'aktualne') ? 'AKTUÁLNE' : tag}
                  </div>
                ))}
                
                {/* Display custom tags if they exist */}
                {listing.customTags && listing.customTags.length > 0 && 
                  listing.customTags.map(tag => (
                    <div
                      key={`custom-${tag}`}
                      style={styles.tag} // Use the same style as regular tags
                    >
                      {tag}
                    </div>
                  ))
                }
              </div>
            )}
            
            {/* If there are no regular tags but there are custom tags */}
            {(!tagsList || tagsList.length === 0) && listing.customTags && listing.customTags.length > 0 && (
              <div style={styles.tagContainer}>
                {listing.customTags.map(tag => (
                  <div
                    key={`custom-${tag}`}
                    style={styles.tag}
                  >
                    {tag}
                  </div>
                ))}
              </div>
            )}

            {/* Property Specs - Show if pocetIzieb or rozloha exists */}
            {(listing.pocetIzieb || listing.rozloha) && (
              <div style={styles.propertySpecs}>
                {/* Room icon => pocet izieb */}
                {listing.pocetIzieb && (
                  <div style={styles.specItem}>
                    <span style={styles.specIcon}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="3" y1="9" x2="21" y2="9"></line>
                        <line x1="9" y1="21" x2="9" y2="9"></line>
                      </svg>
                    </span>
                    <span style={styles.specLabel}>Druh nehnuteľnosti:</span>
                    <span style={styles.specValue}>{listing.pocetIzieb}</span>
                  </div>
                )}
                {/* Area icon => rozloha */}
                {listing.rozloha && (
                  <div style={styles.specItem}>
                    <span style={styles.specIcon}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3h18v18H3z"></path>
                        <path d="M21 3h-6v6h6V3z"></path>
                        <path d="M3 15h6v6H3v-6z"></path>
                      </svg>
                    </span>
                    <span style={styles.specLabel}>Rozloha:</span>
                    <span style={styles.specValue}>{listing.rozloha} m²</span>
                  </div>
                )}
              </div>
            )}

            {/* Image Gallery */}
            <div style={styles.imageGallery}>
              <div style={styles.mainImageContainer}>
                <img
                  src={allPhotos.length > 0 ? allPhotos[0].original : 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22100%22%2F%3E%3Ccircle%20fill%3D%22%23aaa%22%20cx%3D%22100%22%20cy%3D%22100%22%20r%3D%2250%22%2F%3E%3Ccircle%20fill%3D%22%23888%22%20cx%3D%22500%22%20cy%3D%22300%22%20r%3D%2270%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E'}
                  alt={listing.name}
                  style={styles.mainImage}
                  onClick={() => allPhotos.length > 0 ? openLightbox(0) : null}
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22100%22%2F%3E%3Ccircle%20fill%3D%22%23aaa%22%20cx%3D%22100%22%20cy%3D%22100%22%20r%3D%2250%22%2F%3E%3Ccircle%20fill%3D%22%23888%22%20cx%3D%22500%22%20cy%3D%22300%22%20r%3D%2270%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';
                  }}
                />
              </div>

              {allPhotos.length > 1 && (
                <div style={styles.thumbnailContainer}>
                  {allPhotos.slice(1, 5).map((src, index) => (
                    <div 
                      key={index} 
                      style={styles.thumbnail}
                      onClick={() => openLightbox(index + 1)}
                    >
                      <img
                        src={src?.original || 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22100%22%2F%3E%3Ccircle%20fill%3D%22%23aaa%22%20cx%3D%22100%22%20cy%3D%22100%22%20r%3D%2250%22%2F%3E%3Ccircle%20fill%3D%22%23888%22%20cx%3D%22500%22%20cy%3D%22300%22%20r%3D%2270%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E'}
                        alt={`Photo ${index + 2}`}
                        style={styles.thumbnailImage}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22100%22%2F%3E%3Ccircle%20fill%3D%22%23aaa%22%20cx%3D%22100%22%20cy%3D%22100%22%20r%3D%2250%22%2F%3E%3Ccircle%20fill%3D%22%23888%22%20cx%3D%22500%22%20cy%3D%22300%22%20r%3D%2270%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';
                        }}
                      />
                    </div>
                  ))}
                  {allPhotos.length > 5 && (
                    <div 
                      style={styles.morePhotos}
                      onClick={() => openLightbox(5)}
                    >
                      +{allPhotos.length - 5}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Description Section */}
            <div style={styles.descriptionSection}>
              <h2 style={styles.descriptionTitle}>Popis</h2>
              <div style={styles.descriptionContent}>
                {listing.description ? (
                  <p style={styles.descriptionText}>{listing.description}</p>
                ) : (
                  <p style={styles.noDescription}>Žiadny popis nie je k dispozícii.</p>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT side: contact form or empty placeholder */}
          {!tagsList.includes('neplatiči') ? (
            <div className="contactBox" style={styles.contactBox}>
              <h2 style={styles.formHeading}>Napíšte nám správu</h2>
              
              {submitMessage.text && (
                <div 
                  style={{
                    ...styles.messageBox,
                    backgroundColor: 
                      submitMessage.type === 'success' ? 'rgba(223, 242, 216, 0.8)' : 
                      submitMessage.type === 'info' ? 'rgba(217, 237, 247, 0.8)' : 
                      'rgba(248, 215, 218, 0.8)',
                    color: 
                      submitMessage.type === 'success' ? '#3c763d' : 
                      submitMessage.type === 'info' ? '#31708f' : 
                      '#721c24',
                    padding: '15px',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }}
                >
                  {submitMessage.text}
                </div>
              )}

              <form style={styles.form} onSubmit={handleSubmit}>
                <label style={styles.label}>Vaše meno</label>
                <input 
                  type="text" 
                  name="name"
                  value={name}
                  onChange={handleChange}
                  style={styles.input} 
                  className="noFocusOutline" 
                  required
                />

                <label style={styles.label}>Váš email</label>
                <input 
                  type="email" 
                  name="email"
                  value={email}
                  onChange={handleChange}
                  style={styles.input} 
                  className="noFocusOutline" 
                  required
                />

                <label style={styles.label}>Meno inzerátu</label>
                <textarea 
                  value={listing.name} 
                  readOnly
                  className="noFocusOutline autoExpand"
                  style={styles.formTextarea}
                />

                <label style={styles.label}>Vaša správa</label>
                <textarea 
                  rows={5} 
                  name="message"
                  value={message}
                  onChange={handleChange}
                  style={styles.textarea} 
                  className="noFocusOutline" 
                  required
                />
                
                <div style={styles.checkboxContainer}>
                  <input 
                    type="checkbox" 
                    id="gdpr" 
                    name="gdpr"
                    checked={gdpr}
                    onChange={handleChange}
                    style={styles.checkbox} 
                    required 
                  />
                  <label htmlFor="gdpr" style={styles.checkboxLabel}>
                    Súhlasím so spracovaním osobných údajov
                  </label>
                </div>

                <button
                  type="submit"
                  style={{
                    ...styles.submitButton,
                    opacity: isSubmitting ? 0.7 : 1,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                  className="hoverButton"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Odosielanie...' : 'Odoslať'}
                </button>
              </form>

              <div style={styles.contactInfo}>
                <h3 style={styles.contactHeading}>Máte ďalšie otázky?</h3>
                <h3 style={styles.contactHeading}>Kontaktujte nás telefonicky</h3>
                <div style={styles.phoneContainer}>
                  <div style={styles.phoneLabel}>Mobil:</div>
                  <div style={styles.phoneList}>
                    <div style={styles.phoneNumber}>0910 397 308</div>
                    <div style={styles.phoneNumber}>0948 179 171</div>
                    <div style={styles.phoneNumber}>0948 242 741</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={styles.emptyPlaceholder}></div>
          )}
        </div>
      </div>

      <Footer />

      {/* LIGHTBOX if open */}
      {lightboxOpen && allPhotos.length > 0 && (
        <div style={styles.lightboxOverlay} onClick={closeLightbox}>
          <div style={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
            <img
              src={allPhotos[currentIndex]?.original || 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22100%22%2F%3E%3Ccircle%20fill%3D%22%23aaa%22%20cx%3D%22100%22%20cy%3D%22100%22%20r%3D%2250%22%2F%3E%3Ccircle%20fill%3D%22%23888%22%20cx%3D%22500%22%20cy%3D%22300%22%20r%3D%2270%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E'}
              alt={`Photo ${currentIndex + 1}`}
              style={styles.lightboxImage}
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22100%22%2F%3E%3Ccircle%20fill%3D%22%23aaa%22%20cx%3D%22100%22%20cy%3D%22100%22%20r%3D%2250%22%2F%3E%3Ccircle%20fill%3D%22%23888%22%20cx%3D%22500%22%20cy%3D%22300%22%20r%3D%2270%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';
              }}
            />
            <button style={styles.lightboxClose} onClick={closeLightbox}>
              ✕
            </button>
            <button style={styles.lightboxPrev} onClick={goPrev}>
              ‹
            </button>
            <button style={styles.lightboxNext} onClick={goNext}>
              ›
            </button>
            <div style={styles.lightboxCounter}>
              {currentIndex + 1} / {allPhotos.length}
            </div>
          </div>
        </div>
      )}

      {/* Inline <style> block for focus and hover animations */}
      <style>{`
        /* Remove focus outline on inputs/textarea */
        .noFocusOutline {
          outline: none !important;
        }
        .noFocusOutline:focus,
        .noFocusOutline:active,
        .noFocusOutline:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }

        /* Auto-expand textarea */
        .autoExpand {
          display: block;
          overflow: hidden;
          resize: none;
        }

        /* Hover animation on .hoverButton */
        .hoverButton {
          transition: transform 0.3s ease, background-color 0.3s ease;
        }
        .hoverButton:hover {
          transform: scale(1.05);
          background-color: rgba(255,255,255,0.7);
        }
        
        /* Loading spinner animation */
        @keyframes spinner {
          to {transform: rotate(360deg);}
        }
        
        /* Fix for horizontal overflow */
        html, body {
          overflow-x: hidden;
          max-width: 100%;
        }
        
        * {
          box-sizing: border-box;
          max-width: 100%;
        }
        
        img {
          max-width: 100%;
          height: auto;
        }
      `}</style>
    </>
  );
}

export default ListingDetail;

// ===============
// STYLES
// ===============
const styles = {
  container: {
    maxWidth: '1200px',
    margin: '100px auto 40px auto',
    padding: '0 2rem',
    fontFamily: "'Lato', Helvetica, Arial, sans-serif",
    overflowX: 'hidden',
    boxSizing: 'border-box',
    width: '100%',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    margin: '100px auto',
  },
  loadingSpinner: {
    border: '4px solid rgba(0, 0, 0, 0.1)',
    borderLeft: '4px solid #2c69a0',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    animation: 'spinner 1s linear infinite',
    marginBottom: '1rem',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
    color: '#666',
  },
  breadcrumbLink: {
    color: '#2c69a0',
    textDecoration: 'none',
    marginRight: '0.5rem',
  },
  breadcrumbSeparator: {
    margin: '0 0.5rem',
    color: '#999',
  },
  mainContent: {
    display: 'flex',
    gap: '2rem',
    flexWrap: 'wrap',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  leftSide: {
    flex: '1 1 600px',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  mainHeading: {
    fontSize: '2.2rem',
    fontWeight: '700',
    marginBottom: '1.5rem',
    color: '#333',
    maxWidth: '100%',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
  
  // Image Gallery
  imageGallery: {
    marginBottom: '2rem',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  mainImageContainer: {
    width: '100%',
    height: '450px',
    overflow: 'hidden',
  },
  mainImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    cursor: 'pointer',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.03)',
    },
  },
  thumbnailContainer: {
    display: 'flex',
    height: '100px',
    marginTop: '4px',
    gap: '4px',
  },
  thumbnail: {
    width: 'calc(25% - 3px)',
    overflow: 'hidden',
    cursor: 'pointer',
    position: 'relative',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
  morePhotos: {
    width: 'calc(25% - 3px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    cursor: 'pointer',
  },
  
  // Property Info
  infoSection: {
    padding: '0.5rem',
  },
  priceSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    padding: '1rem 0',
    borderBottom: '1px solid #eee',
  },
  price: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#2c69a0',
    margin: 0,
  },
  location: {
    display: 'flex',
    alignItems: 'center',
    color: '#666',
    fontSize: '1rem',
  },
  locationIcon: {
    marginRight: '0.5rem',
    fontSize: '1.2rem',
  },
  
  // Tags
  tagContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    maxHeight: '120px', // Allow up to 4 lines of tags
    overflow: 'auto', // Allow scrolling if needed
    paddingBottom: '0.5rem', // Add some padding at the bottom
  },
  tag: {
    backgroundColor: '#f0f0f0',
    color: '#333',
    padding: '0.4rem 0.8rem',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '500',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  tagNeplatici: {
    backgroundColor: '#ffeaea',
    color: '#d83a3a',
    padding: '0.4rem 0.8rem',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '500',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  tagAktualne: {
    backgroundColor: '#28a745', // Green color for aktuálne
    color: 'white',
    padding: '0.4rem 0.8rem',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: 'bold',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  tagPredane: {
    backgroundColor: '#cc0000',
    color: 'white',
    padding: '0.4rem 0.8rem',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: 'bold',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  tagPrenajate: {
    backgroundColor: '#ff8c00', // Orange color
    color: 'white',
    padding: '0.4rem 0.8rem',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: 'bold',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  
  // Property Specs
  propertySpecs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1.5rem',
    backgroundColor: '#f9f9f9',
    padding: '1rem 1.5rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  specItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  specIcon: {
    fontSize: '1.2rem',
    color: '#555',
  },
  areaIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#222',
    width: '30px',
    height: '30px',
    position: 'relative',
    border: '2px solid #222',
    borderRadius: '0',
    borderRight: 'none',
    paddingRight: '0',
    marginRight: '0',
    lineHeight: '1',
    backgroundColor: 'white',
  },
  specLabel: {
    color: '#777',
    fontSize: '0.9rem',
    marginRight: '0.3rem',
  },
  specValue: {
    fontWeight: 'bold',
    color: '#333',
    fontSize: '0.95rem',
  },
  
  // Description
  descriptionSection: {
    marginTop: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  descriptionTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    color: '#333',
  },
  descriptionContent: {
    fontSize: '1rem',
    lineHeight: '1.6',
    color: '#555',
  },
  descriptionText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  noDescription: {
    color: '#999',
    fontStyle: 'italic',
  },
  
  // CONTACT BOX - Keeping mostly the same but with minor improvements
  contactBox: {
    flex: '1 1 350px',
    background: `
      linear-gradient(
        45deg,
        rgb(44, 105, 160) 0%,
        rgb(100, 90, 255) 100%
      )
    `,
    borderRadius: '8px',
    padding: '2rem',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'flex-start',
    position: 'sticky',
    top: '100px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
    overflow: 'visible',
    maxHeight: 'none',
    maxWidth: '100%',
    boxSizing: 'border-box',
    height: 'auto',
    minHeight: 'min-content',
  },
  formHeading: {
    fontSize: '2.3rem',
    marginBottom: '1.5rem',
    fontWeight: '600',
  },
  form: {
    marginBottom: '2rem',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  contactInfo: {
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.3)',
    width: '100%',
    wordBreak: 'break-word',
    overflow: 'visible',
  },
  contactHeading: {
    fontSize: '1.2rem',
    fontWeight: '600',
    margin: '0 0 0.5rem 0',
    color: 'white',
    wordBreak: 'break-word',
  },
  phoneContainer: {
    marginTop: '0.5rem',
    width: '100%',
    wordBreak: 'break-word',
    marginBottom: '1rem',
  },
  phoneLabel: {
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    color: 'white',
  },
  phoneList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    width: '100%',
    marginBottom: '1.5rem',
  },
  phoneNumber: {
    color: 'white',
    fontWeight: '500',
    wordBreak: 'break-word',
  },
  label: {
    fontWeight: 'bold',
    margin: '1rem 0 0.5rem',
    fontSize: '0.9rem',
    display: 'block',
    width: '100%',
  },
  input: {
    width: '100%',
    padding: '0.7rem 0',
    marginBottom: '1rem',
    border: 'none',
    borderBottom: '2px solid #fff',
    backgroundColor: 'transparent',
    color: '#fff',
    fontSize: '1rem',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '0.7rem 0',
    marginBottom: '1.5rem',
    border: 'none',
    borderBottom: '2px solid #fff',
    backgroundColor: 'transparent',
    color: '#fff',
    resize: 'vertical',
    fontSize: '1rem',
  },
  submitButton: {
    backgroundColor: '#fff',
    color: '#2c69a0',
    padding: '0.8rem 1.5rem',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    alignSelf: 'flex-start',
    fontSize: '1rem',
    marginTop: '0.5rem',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  },
  extraHeading: {
    marginTop: 0,
    marginBottom: '0.8rem',
    fontSize: '1.2rem',
    fontWeight: '600',
    color: '#fff',
    backgroundColor: 'transparent',
    paddingTop: '1rem',
  },
  extraText: {
    margin: 0,
    fontSize: '1rem',
    lineHeight: '1.7',
    color: '#fff',
    backgroundColor: 'transparent',
    fontWeight: '500',
  },
  phoneNumbers: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  phoneNumber: {
    margin: 0,
    fontSize: '1rem',
    lineHeight: '1.2',
    color: '#fff',
    backgroundColor: 'transparent',
    fontWeight: '500',
  },

  // LIGHTBOX
  lightboxOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  lightboxContent: {
    position: 'relative',
    maxWidth: '90%',
    maxHeight: '90%',
  },
  lightboxImage: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: '80vh',
    margin: '0 auto',
    borderRadius: '4px',
    boxShadow: '0 5px 25px rgba(0,0,0,0.2)',
  },
  lightboxClose: {
    position: 'absolute',
    top: '-40px',
    right: '0',
    color: '#fff',
    fontSize: '2rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  lightboxPrev: {
    position: 'absolute',
    top: '50%',
    left: '-60px',
    transform: 'translateY(-50%)',
    fontSize: '3rem',
    color: '#fff',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    paddingBottom: '8px',
  },
  lightboxNext: {
    position: 'absolute',
    top: '50%',
    right: '-60px',
    transform: 'translateY(-50%)',
    fontSize: '3rem',
    color: '#fff',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    paddingBottom: '8px',
  },
  lightboxCounter: {
    position: 'absolute',
    bottom: '-30px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: 'white',
    fontWeight: '500',
  },

  // Location section for neplatiči listings
  locationSection: {
    marginBottom: '1rem',
  },
  // Empty placeholder to maintain layout when contact form is hidden
  emptyPlaceholder: {
    flex: '1 1 350px',
    minHeight: '200px', // Minimal height to ensure some space
  },

  formTextarea: {
    width: '100%',
    marginBottom: '1rem',
    padding: '0.7rem 0',
    border: 'none',
    borderBottom: '2px solid #fff',
    backgroundColor: 'transparent',
    color: '#fff',
    resize: 'none',
  },

  // Add these new styles
  messageBox: {
    padding: '12px 16px',
    marginBottom: '20px',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  checkbox: {
    marginRight: '10px',
  },
  checkboxLabel: {
    color: '#fff',
    fontSize: '0.9rem',
  },

  // Back Button
  backButtonContainer: {
    padding: '1rem',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  backButton: {
    background: '#2c69a0',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    cursor: 'pointer',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    marginBottom: '1rem',
  },
};
